import { useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { attempts, questions } from '../lib/api';
import { useApi } from '../hooks/useApi';
import { useTranslation } from '../i18n';
import { playSound } from '../lib/sound';

export default function QuizPage() {
  const { t } = useTranslation();
  const { bookId = '' } = useParams();
  const state = useApi(() => questions.byBook(bookId), [bookId]);
  const items = state.status === 'success' ? state.data.items : [];

  const [idx, setIdx] = useState(0);
  const [chosen, setChosen] = useState<string | null>(null);
  const [result, setResult] = useState<{ ok: boolean; theta: number } | null>(null);
  const [stats, setStats] = useState<{ correct: number; finalTheta: number } | null>(null);

  const current = items[idx];
  const choices = useMemo(() => current?.choices ?? [], [current]);

  async function submit() {
    if (!current || !chosen) return;
    try {
      const r = await attempts.submit(current.id, chosen);
      setResult({ ok: r.is_correct, theta: r.theta_after });
      playSound(r.is_correct ? 'correct' : 'wrong');
    } catch {
      setResult({ ok: false, theta: 0 });
    }
  }

  function next() {
    const isLast = idx >= items.length - 1;
    if (isLast && result) {
      setStats((prev) => ({
        correct: (prev?.correct ?? 0) + (result.ok ? 1 : 0),
        finalTheta: result.theta,
      }));
      playSound('celebrate');
      return;
    }
    setStats((prev) => ({
      correct: (prev?.correct ?? 0) + (result?.ok ? 1 : 0),
      finalTheta: result?.theta ?? prev?.finalTheta ?? 0,
    }));
    setChosen(null);
    setResult(null);
    setIdx((i) => Math.min(i + 1, items.length - 1));
  }

  if (state.status === 'loading') return <div className="container"><p className="muted">{t('common.loading')}</p></div>;
  if (state.status === 'error' || items.length === 0) {
    return <div className="container"><p className="muted">{t('quiz.noQuestions')}</p></div>;
  }

  const isLast = idx >= items.length - 1;
  const showCelebration = isLast && result != null && stats != null;
  const totalAnswered = items.length;

  return (
    <div className="quiz-layout">
      <div>
        <div className="q-progress">
          <div className="q-progress-bar">
            <div className="q-progress-fill" style={{ width: `${((idx + 1) / items.length) * 100}%` }} />
          </div>
          <div className="q-progress-meta">
            <span>{idx + 1}{t('quiz.of')}{items.length}</span>
            <span>{current.tier} · {current.estimated_time_sec}s</span>
          </div>
        </div>

        <article className="question">
          <div className="q-meta">
            <span className={`q-tier-tag q-tier-${current.tier?.toLowerCase()}`}>{current.tier}</span>
            {current.ccss_code && <span className="chip chip-mute">{current.ccss_code}</span>}
            {current.dok_level != null && <span className="chip chip-mute">DOK {current.dok_level}</span>}
            {current.lexile_measure != null && <span className="chip chip-mute">Lexile {current.lexile_measure}L</span>}
          </div>

          <h2 className="q-stem">{current.stem}</h2>

          <div className="q-choices">
            {choices.map((c) => {
              const sel = chosen === c.label;
              const status = result == null ? '' :
                c.label === current.correct_choice ? 'correct' :
                sel ? 'incorrect' : '';
              return (
                <button
                  key={c.label}
                  className={`q-choice q-choice-btn ${sel ? 'selected' : ''} ${status}`}
                  onClick={() => result == null && setChosen(c.label)}
                  type="button"
                  disabled={result != null}
                >
                  <span className="q-letter">{c.label}</span>
                  <span className="q-text">{c.text}</span>
                </button>
              );
            })}
          </div>

          <div className="q-actions">
            <div className="q-timer">⏱ —</div>
            <div className="row-8">
              {result == null ? (
                <>
                  <button className="btn btn-ghost" type="button">{t('common.skip')}</button>
                  <button className="btn btn-accent" onClick={submit} disabled={!chosen}>
                    {t('common.submit')} <span className="arrow">→</span>
                  </button>
                </>
              ) : (
                <button className="btn btn-primary" onClick={next}>
                  {isLast ? t('common.finish') : t('common.next')} <span className="arrow">→</span>
                </button>
              )}
            </div>
          </div>

          {result && (
            <div className="feedback" style={{ borderTop: '1px solid var(--bq-color-border-strong)' }}>
              <div className="fb-head">
                <span className="fb-mark">{result.ok ? '✓' : '✕'}</span>
                <span className="fb-title">{result.ok ? t('common.correct') : t('common.notQuite')}</span>
                <span className="fb-delta">θ {result.theta >= 0 ? '+' : ''}{result.theta.toFixed(2)}</span>
              </div>
            </div>
          )}
        </article>
      </div>

      <aside className="quiz-side">
        <div className="side-block">
          <div className="side-label">{t('quiz.tier')}</div>
          <div className="theta-big">
            <span className="theta-value">{current.tier}</span>
          </div>
          <div className="theta-meta">{current.item_format} · {current.estimated_time_sec}s</div>
        </div>
      </aside>

      {showCelebration && stats && (
        <div className="quiz-celebration" role="dialog" aria-modal="true" aria-labelledby="quiz-celeb-title">
          <div className="quiz-celebration__panel">
            <div className="quiz-celebration__mark" aria-hidden="true">★</div>
            <div className="quiz-celebration__kicker">{t('quiz.celebration.kicker')}</div>
            <h2 id="quiz-celeb-title" className="quiz-celebration__title">
              {t('quiz.celebration.titleLine1')} <em>{t('quiz.celebration.titleLine2')}</em>
            </h2>
            <p className="quiz-celebration__lead">{t('quiz.celebration.lead')}</p>

            <div className="quiz-celebration__stats">
              <div className="quiz-celebration__stat">
                <span className="quiz-celebration__stat-label">{t('quiz.celebration.correct')}</span>
                <span className="quiz-celebration__stat-value">
                  {stats.correct}/{totalAnswered}
                </span>
              </div>
              <div className="quiz-celebration__stat">
                <span className="quiz-celebration__stat-label">{t('quiz.celebration.theta')}</span>
                <span className="quiz-celebration__stat-value">
                  {stats.finalTheta >= 0 ? '+' : ''}{stats.finalTheta.toFixed(2)}
                </span>
              </div>
            </div>

            <div className="quiz-celebration__actions">
              <Link to={`/books/${bookId}/report`} className="btn btn-accent">
                {t('quiz.celebration.viewReport')} <span className="arrow">→</span>
              </Link>
              <Link to="/credentials" className="btn btn-secondary">
                {t('quiz.celebration.viewCredential')}
              </Link>
              <Link to={`/books/${bookId}`} className="btn btn-ghost">
                {t('common.back')}
              </Link>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
